// Self-contained settings dialog for reordering and hiding top-nav items.
// Presentational only: the Navbar owns the useNavPreferences state and passes
// the items + mutators in as props, so changes reflect in the nav live.
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

import { OrderedNavItem } from '../hooks/useNavPreferences';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

export interface NavSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  items: OrderedNavItem[];
  onToggleHidden: (path: string) => void;
  onMoveUp: (path: string) => void;
  onMoveDown: (path: string) => void;
  onReset: () => void;
}

const NavSettingsDialog: React.FC<NavSettingsDialogProps> = ({
  open,
  onClose,
  items,
  onToggleHidden,
  onMoveUp,
  onMoveDown,
  onReset,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="nav-settings-title"
    >
      <DialogTitle id="nav-settings-title">Navigation settings</DialogTitle>
      <DialogContent dividers sx={dialogContentPaddingSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Reorder the navigation or hide items you don&apos;t use. The account
          menu always stays available.
        </Typography>
        <List disablePadding>
          {items.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === items.length - 1;
            return (
              <ListItem key={item.path} disableGutters divider>
                <ListItemText
                  primary={item.label}
                  sx={{ opacity: item.hidden ? 0.5 : 1, pr: 1 }}
                />
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Move ${item.label} up`}
                      disabled={isFirst}
                      onClick={() => onMoveUp(item.path)}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Move ${item.label} down`}
                      disabled={isLast}
                      onClick={() => onMoveDown(item.path)}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={item.hidden ? 'Show' : 'Hide'}>
                  <IconButton
                    size="small"
                    aria-label={`${item.hidden ? 'Show' : 'Hide'} ${item.label}`}
                    onClick={() => onToggleHidden(item.path)}
                  >
                    {item.hidden ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReset}>Reset to default</Button>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NavSettingsDialog;
